import React, { useEffect, useState } from "react";
import { Add, DirectionsCar } from "@mui/icons-material";
import { Box, Button, Paper, Typography, Tabs, Tab, Divider } from "@mui/material";
import { toast } from "react-toastify";
import { useSearchParams } from "react-router-dom";

import FormDrawer from "components/Shared/FormDrawer";

import { useRouteShipmentDetailContext } from "contexts/RouteShipmentDetailContext";

import RouteVehicleDepartureList from "./RouteDriverVehicleList";
import RouteVehicleDepartureForm from "./RouteDriverVehicleForm";
import { useAddDriverVehicleDepartureMutation, useLazyListDriverVehicleDepartureQuery, useUpdateDriverVehicleDepartureMutation } from "store/services/routeDepartureVehicleApi";

const RouteVehicleDepartureAssign = () => {
    const [showDepartureForm, setShowDepartureForm] = useState(false);
    const [selectedDeparture, setSelectedDeparture] = useState(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const activeDepartureTab = searchParams.get("depTab") || "Active";
    const isActiveTab = activeDepartureTab === "Active";

    const handleDepartureTabChange = (event, newValue) => {
        const newParams = new URLSearchParams(searchParams);
        newParams.set("depTab", newValue);
        setSearchParams(newParams);
    };

    const { routeShipmentDetail } = useRouteShipmentDetailContext();
    const routeShipmentId = routeShipmentDetail?._id;
    const isActive = routeShipmentDetail?.isActive;

    const [
        fetchDepartures,
        { data: departuresRes, isLoading },
    ] = useLazyListDriverVehicleDepartureQuery();
    const [addDeparture, { isLoading: addingDeparture }] =
        useAddDriverVehicleDepartureMutation();
    const [updateDeparture, { isLoading: updatingDeparture }] =
        useUpdateDriverVehicleDepartureMutation();

    useEffect(() => {
        if (routeShipmentId && isActive) {
            fetchDepartures({
                route: routeShipmentId,
                active: isActiveTab ? "true" : "false"
            }).catch(() => {
                toast.error("Failed to fetch departures!");
            });
        }
    }, [routeShipmentId, fetchDepartures, isActive, isActiveTab]);

    const handleCancelDeparture = () => {
        setShowDepartureForm(false);
        setSelectedDeparture(null);
    };

    const handleAddDeparture = async (data) => {
        if (!routeShipmentId) {
            toast.error("Route shipment ID is missing!");
            return;
        }

        const payload = {
            account: data.account,
            entity: data.entity,
            route: routeShipmentId,
            driver: data.driver,
            vehicle: data.vehicle,
            schedules: data.schedules,
            isActive: data.isActive,
        };

        try {
            await addDeparture(payload).unwrap();
            toast.success("Departure added successfully!");
            handleCancelDeparture();
            fetchDepartures({ route: routeShipmentId });
        } catch (error) {
            toast.error(error?.data?.message || "Failed to add departure!");
        }
    };

    const handleUpdateDeparture = async (data) => {
        if (!routeShipmentId) {
            toast.error("Route shipment ID is missing!");
            return;
        }

        const payload = {
            account: data.account,
            entity: data.entity,
            route: routeShipmentId,
            driver: data.driver,
            vehicle: data.vehicle,
            schedules: data.schedules,
            isActive: data.isActive,
        };

        try {
            await updateDeparture({
                _id: selectedDeparture._id,
                ...payload,
            }).unwrap();
            toast.success("Departure updated successfully!");
            handleCancelDeparture();
            fetchDepartures({ route: routeShipmentId });
        } catch (error) {
            toast.error(error?.data?.message || "Failed to update departure!");
        }
    };

    const departures = Array.isArray(departuresRes?.data)
        ? departuresRes.data
        : [];

    return (
        <>
            {isActive ? (
                <>
                    <Paper>
                        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                            <Tabs
                                value={activeDepartureTab}
                                onChange={handleDepartureTabChange}
                                aria-label="departure tabs"
                            >
                                <Tab label="Active" value="Active" />
                                <Tab label="Inactive" value="Inactive" />
                            </Tabs>
                        </Box>

                        <Box display="flex" alignItems="end" justifyContent="end" padding={3}>
                            {!showDepartureForm && (
                                <Button
                                    startIcon={<Add />}
                                    variant="outlined"
                                    onClick={() => setShowDepartureForm(true)}
                                >
                                    Add Departure
                                </Button>
                            )}
                        </Box>

                        <Divider />

                        <RouteVehicleDepartureList
                            departures={departures}
                            isLoading={isLoading}
                            onEdit={(departure) => {
                                setSelectedDeparture(departure);
                                setShowDepartureForm(true);
                            }}
                        />
                    </Paper>

                    <FormDrawer
                        title={
                            selectedDeparture ? "Edit Route Departure" : "Add Route Departure"
                        }
                        open={showDepartureForm}
                        onClose={handleCancelDeparture}
                    >
                        <RouteVehicleDepartureForm
                            isBusy={selectedDeparture ? updatingDeparture : addingDeparture}
                            departure={selectedDeparture}
                            routeId={routeShipmentId}
                            onCancel={handleCancelDeparture}
                            onSubmit={
                                selectedDeparture ? handleUpdateDeparture : handleAddDeparture
                            }
                        />
                    </FormDrawer>
                </>
            ) : (
                <Box
                    sx={{
                        flex: 1,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        bgcolor: "background.paper",
                        borderRadius: 2,
                        p: 4,
                    }}
                >
                    <Paper
                        elevation={0}
                        sx={{
                            p: 4,
                            textAlign: "center",
                            maxWidth: 500,
                            borderRadius: 2,
                            width: "100%",
                        }}
                    >
                        <Box
                            sx={{
                                bgcolor: "action.hover",
                                width: 80,
                                height: 80,
                                borderRadius: "50%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                mx: "auto",
                                mb: 3,
                            }}
                        >
                            <DirectionsCar color="disabled" sx={{ fontSize: 40 }} />
                        </Box>
                        <Typography variant="h5" color="text.secondary" gutterBottom>
                            Departures Unavailable
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            You can only manage departures when the Route Shipment is active.
                        </Typography>
                    </Paper>
                </Box>
            )
            }
        </>
    );
};

export default RouteVehicleDepartureAssign;